import { Eye, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/class-control";
import type { INotification } from "@/interfaces/notification.interface";
import { NotificationStatusBadge } from "@/components/ui/notification-status-badge";
import { NotificationTypeIcon } from "@/components/ui/notification-type-icon";

export interface NotificationTableProps {
  notifications: INotification[];
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function NotificationTable({
  notifications,
  onView,
  onEdit,
  onDelete,
}: NotificationTableProps) {
  const handleDelete = (id: string, message: string) => {
    if (
      onDelete &&
      confirm(`Tem certeza que deseja excluir esta notificação?\n"${message}"`)
    ) {
      onDelete(id);
    }
  };

  return (
    <div className="flex flex-col gap-px items-start w-full">
      {/* Table Header */}
      <div className="bg-[#0C1427] box-border flex gap-[59px] h-[42px] items-center px-[20px] py-[10px] rounded-[8px] w-full">
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[160px]">
          ID
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[160px]">
          Data
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[220px]">
          Tipo
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[392px]">
          Notificação
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[120px]">
          Status
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[123px]">
          Ações
        </p>
      </div>

      {/* Table Rows */}
      {notifications.map((notification, index) => (
        <div key={notification.id}>
          <div className="box-border flex gap-[59px] items-center px-[20px] py-[10px] w-full">
            {/* ID */}
            <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[160px]">
              <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
                {notification.id}
              </p>
            </div>

            {/* Data */}
            <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] w-[160px]">
              {notification.date}
            </p>

            {/* Tipo */}
            <div className="flex gap-[10px] items-center w-[220px]">
              <NotificationTypeIcon type={notification.type} size="md" />
              <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px]">
                {notification.type}
              </p>
            </div>

            {/* Notificação */}
            <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[392px]">
              <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
                {notification.message}
              </p>
            </div>

            {/* Status */}
            <div className="flex gap-[10px] items-center justify-center w-[120px]">
              <NotificationStatusBadge status={notification.status} />
            </div>

            {/* Ações */}
            <div className="box-border flex gap-[10px] items-center p-[10px] rounded-[30px] w-[123px]">
              <button
                onClick={() => onView?.(notification.id)}
                className={cn(
                  "overflow-clip shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-purple-400 text-gray-400"
                )}
                title="Visualizar"
                aria-label="Visualizar notificação"
              >
                <Eye className="w-full h-full" />
              </button>
              <button
                onClick={() => onEdit?.(notification.id)}
                className={cn(
                  "shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-purple-400 text-gray-400"
                )}
                title="Editar"
                aria-label="Editar notificação"
              >
                <Pencil className="w-full h-full" />
              </button>
              <button
                onClick={() => handleDelete(notification.id, notification.message)}
                className={cn(
                  "shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-red-500 text-gray-400"
                )}
                title="Excluir"
                aria-label="Excluir notificação"
              >
                <Trash2 className="w-full h-full" />
              </button>
            </div>
          </div>

          {/* Divider Line */}
          {index < notifications.length - 1 && (
            <div className="h-0 w-full relative">
              <div className="absolute bottom-0 left-0 right-0 top-[-1px] border-b border-[#2a2f4a]" />
            </div>
          )}
        </div>
      ))}

      {/* Empty State */}
      {notifications.length === 0 && (
        <div className="text-center py-12 w-full">
          <p className="text-gray-400 text-sm">Nenhuma notificação encontrada</p>
        </div>
      )}
    </div>
  );
}
